import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckCircle2, MapPin, Phone, Store, Ticket, Users } from 'lucide-react'
import Header from '../../../components/Header'
import { useI18n } from '../../../i18n/useI18n'
import { api, type PublicExperienceShare as ShareData } from '../../../services/api'
import { formatSlotDateTime } from '../../../utils/experienceSlot'

/**
 * 体験チケットの公開共有ページ (#1291) — 未ログインで閲覧可。
 *
 * `/e/{shareCode}` で来訪。予約した本人が友達に共有したリンクの行き先。
 * バックエンド (`/api/v1/experience/share/{shareCode}`) は消し込み code と PII を
 * 除外した「予約内容の要約」(体験名 / 店舗 / 何名分 / 予約済みか) を返すので、
 * 友達は「本当に予約したこと」を確認できる (店舗ページの共有ではダメ、という FB)。
 */
export default function PublicExperienceShare() {
  const { shareCode } = useParams<{ shareCode: string }>()
  const navigate = useNavigate()
  const { t, locale } = useI18n()
  const [data, setData] = useState<ShareData | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!shareCode) {
      setError(true)
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.getPublicExperienceShare(shareCode)
        if (!cancelled) setData(res)
      } catch {
        if (!cancelled) setError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [shareCode])

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) navigate(-1)
    else navigate('/home')
  }

  const name = data ? (locale === 'ja' ? data.productName : data.productNameEn || data.productName) : ''
  const total = data ? data.priceJpy * data.count : 0
  const mapUrl = (s: ShareData['store']) =>
    s.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || s.name || '')}`

  return (
    <div className="min-h-screen bg-warm-white safe-top pb-20">
      <Header title={t('publicReservation.title')} onBack={handleBack} />

      {loading ? (
        <p className="text-center py-16 font-body text-sm text-text-tertiary">...</p>
      ) : error || !data ? (
        <div className="px-6 pt-16 text-center">
          <p className="font-display text-lg font-semibold text-text-primary mb-2">
            {t('publicReservation.error.not_found.title')}
          </p>
          <p className="font-body text-sm text-text-secondary mb-6">
            {t('publicReservation.error.not_found.detail')}
          </p>
          <button
            onClick={handleBack}
            className="rounded-xl bg-yokoso-red px-6 py-3 text-white font-display font-semibold tap-effect"
          >
            {t('publicReservation.error.cta')}
          </button>
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-4">
          {/* 体験チケット detail */}
          <div className="bg-white rounded-2xl shadow-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-2.5 py-0.5 text-[11px] font-body font-semibold text-gold-dark">
                <Ticket size={12} />
                {t('experience.shareTicketLabel')}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-matcha/15 px-2.5 py-0.5 text-[11px] font-body font-semibold text-matcha">
                <CheckCircle2 size={12} />
                {t('experience.shareBooked')}
              </span>
            </div>
            <p className="font-display font-bold text-lg text-text-primary mb-2">{name}</p>
            <div className="space-y-1.5 font-body text-sm text-text-primary">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-text-secondary shrink-0" />
                <span>{t('publicReservation.partySize', { count: String(data.count) })}</span>
                {data.redeemedCount > 0 && (
                  <span className="text-xs text-text-tertiary">
                    ({t('experience.shareRedeemed', { count: String(data.redeemedCount) })})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Ticket size={16} className="text-text-secondary shrink-0" />
                <span>
                  {data.slotDate
                    ? t('experience.slotOfDay', { datetime: formatSlotDateTime(data.slotDate, data.slotStartTime, locale) })
                    : t('experience.scheduleFree')}
                </span>
              </div>
              <div className="flex items-center gap-2 text-text-secondary">
                <span className="ml-[26px]">
                  ¥{data.priceJpy.toLocaleString()} × {data.count} = ¥{total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Store info */}
          <div className="bg-white rounded-2xl shadow-card p-4">
            <p className="font-display font-bold text-base text-text-primary mb-1">
              {data.store.name || t('publicReservation.unknownStore')}
            </p>
            <div className="space-y-1 mb-3">
              {data.store.address && (
                <p className="font-body text-xs text-text-secondary">
                  <MapPin size={12} className="inline mr-1 -mt-0.5" />
                  {data.store.address}
                </p>
              )}
              {data.store.phoneNumber && (
                <a
                  href={`tel:${data.store.phoneNumber}`}
                  className="font-body text-xs text-text-secondary flex items-center gap-1"
                >
                  <Phone size={12} className="shrink-0" />
                  {data.store.phoneNumber}
                </a>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {data.store.id && (
                <button
                  onClick={() => navigate(`/stores/${data.store.id}`)}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-body font-semibold text-navy tap-effect"
                >
                  <Store size={12} />
                  {t('publicReservation.viewStore')}
                </button>
              )}
              {(data.store.googleMapsUrl || data.store.address) && (
                <a
                  href={mapUrl(data.store)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-body font-semibold text-navy tap-effect"
                >
                  {t('publicReservation.openInMaps')}
                </a>
              )}
            </div>
          </div>

          {/* CTA: 友達も同じ体験を予約できる導線 */}
          {data.store.id && (
            <button
              onClick={() => navigate(`/stores/${data.store.id}`)}
              className="flex w-full h-12 items-center justify-center gap-2 rounded-xl bg-yokoso-red text-white font-display font-semibold text-sm tap-effect"
            >
              <Ticket size={16} />
              {t('experience.shareCta')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
